import React, { useState, useEffect, useRef } from 'react';
import {
   Box,
   Typography,
   Container,
   Paper,
   Grid,
   Button,
   CircularProgress,
   Alert,
   Card,
   CardContent,
   CardMedia,
   Chip,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Divider,
   Stack,
   Avatar,
   IconButton,
   Snackbar,
   AppBar,
   Toolbar,
   Tabs,
   Tab,
   LinearProgress,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   Event as EventIcon,
   Place as PlaceIcon,
   DateRange as DateRangeIcon,
   People as PeopleIcon,
   Share as ShareIcon,
   Close as CloseIcon,
   PersonAdd as PersonAddIcon,
   Person as PersonIcon,
   AccessTime as AccessTimeIcon,
   Public as PublicIcon,
   Check as CheckIcon,
   Link as LinkIcon,
   Folder as FolderIcon,
   Lock as LockIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { Event } from '../../types/Event';
import { format } from 'date-fns';
import { generateFingerprint } from '../../utils/browserFingerprint';
import { getPublicViewAnalyticsPayload } from '../../utils/analyticsVisitor';
import { useUser } from '../../contexts/UserContext';
import EventSignupModal from '../../components/auth/EventSignupModal';
// Font constants (matching theme)
const FONT_DISPLAY = '"Cormorant Garamond", serif';
const FONT_BODY = '"DM Sans", sans-serif';
const FONT_MONO = '"DM Mono", monospace';

const PublicEventPage: React.FC = () => {
   const { eventId } = useParams<{ eventId: string }>();
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

   // Preserve all existing state
   const [event, setEvent] = useState<Event | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [rsvpModalOpen, setRsvpModalOpen] = useState(false);
   const [signupModalOpen, setSignupModalOpen] = useState(false);
   const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [name, setName] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>(
      'success'
   );
   const [viewRecorded, setViewRecorded] = useState(false);

   // New UI state only
   const [activeTab, setActiveTab] = useState(0);
   const [hasAccess, setHasAccess] = useState(false);
   const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
   const [copiedLink, setCopiedLink] = useState(false);
   const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
   const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

   // RSVP form state
   const [rsvpFirstName, setRsvpFirstName] = useState('');
   const [rsvpLastName, setRsvpLastName] = useState('');
   const [rsvpRole, setRsvpRole] = useState('');
   const [rsvpMessage, setRsvpMessage] = useState('');
   const [rsvpCity, setRsvpCity] = useState('');
   const [rsvpPassword, setRsvpPassword] = useState('');
   const [rsvpConfirmPassword, setRsvpConfirmPassword] = useState('');
   const [userExists, setUserExists] = useState(false);

   // Album and capacity state
   const [albumCount, setAlbumCount] = useState(0);
   const [approvedAttendeesCount, setApprovedAttendeesCount] = useState(0);
   const [maxCapacity, setMaxCapacity] = useState(0);
   
   // Participants state
   const [participants, setParticipants] = useState<any[]>([]);
   const [eventHost, setEventHost] = useState<any>(null);

   useEffect(() => {
      const fetchEvent = async () => {
         if (!eventId) return;

         try {
            setLoading(true);
            console.log(`Fetching public event with ID: ${eventId}`);

            // Use backend API to fetch public event
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/public/${eventId}`
            );

            if (!response.ok) {
               const errorData = await response.json();
               console.error('API Error:', errorData);
               setError(errorData.error || 'Event not found');
               return;
            }

            const data = await response.json();
            console.log('Event data received:', data);

            if (!data.success || !data.event) {
               setError('Event not found');
               return;
            }

            setEvent(data.event as Event);

            // Use server-enriched participants/members (profiles from API; no client Firestore reads)
            const eventParticipants = data.event?.participants || [];
            const eventMembers = data.event?.members || [];
            const rawList = [...eventParticipants, ...eventMembers];
            const seenIds = new Set<string>();
            const participantsList = rawList.filter((p: any) => {
               const uid = p.userId || p.id;
               if (!uid || seenIds.has(uid)) return false;
               seenIds.add(uid);
               return true;
            });
            setParticipants(participantsList);

            // Host from API (server sends hostProfile with displayName, photoURL)
            const hostProfile = data.event?.hostProfile;
            if (hostProfile) {
               setEventHost({
                  userId: hostProfile.userId ?? data.event?.userId ?? data.event?.createdBy,
                  displayName: hostProfile.displayName ?? 'Event Host',
                  photoURL: hostProfile.photoURL ?? null,
                  profileData: {
                     displayName: hostProfile.displayName,
                     photoURL: hostProfile.photoURL,
                  },
               });
            } else {
               const ownerId = data.event?.userId || data.event?.createdBy;
               const foundInList = participantsList.find(
                  (p: any) =>
                     p.role === 'owner' ||
                     p.role === 'host' ||
                     data.event?.userId === (p.userId || p.id) ||
                     data.event?.createdBy === (p.userId || p.id)
               );
               if (foundInList) {
                  setEventHost({
                     ...foundInList,
                     profileData: foundInList.profileData || {
                        displayName: foundInList.displayName,
                        photoURL: foundInList.photoURL,
                     },
                  });
               } else if (ownerId) {
                  setEventHost({
                     userId: ownerId,
                     displayName: 'Event Host',
                     photoURL: null,
                     profileData: { displayName: 'Event Host', photoURL: null },
                  });
               } else {
                  setEventHost(null);
               }
            }
         } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event details');
         } finally {
            setLoading(false);
         }
      };

      fetchEvent();
   }, [eventId]);

   // Fetch album count
   useEffect(() => {
      const fetchAlbumCount = async () => {
         if (!eventId) return;

         try {
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/albums`
            );
            
            if (response.ok) {
               const albums = await response.json();
               setAlbumCount(Array.isArray(albums) ? albums.length : 0);
            }
         } catch (error) {
            console.error('Error fetching album count:', error);
            setAlbumCount(0);
         }
      };

      fetchAlbumCount();
   }, [eventId]);

   // Fetch approved attendees count for capacity calculation
   useEffect(() => {
      const fetchApprovedCount = async () => {
         if (!eventId) return;

         try {
            // Use public endpoint to get approved count and max capacity
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/approved-count`
            );

            if (response.ok) {
               const data = await response.json();
               if (data.success) {
                  setApprovedAttendeesCount(data.count || 0);
                  setMaxCapacity(data.maxCapacity || 0);
               }
            } else {
               // Fallback to event members if endpoint fails
               if (event?.members) {
                  const approvedMembers = event.members.filter((m: any) => m.status === 'accepted' || m.status === 'approved');
                  setApprovedAttendeesCount(approvedMembers.length);
               }
               // Get maxCapacity from event data
               const eventMaxCapacity = (event as any)?.maxCapacity || (event as any)?.capacity || (event as any)?.maxParticipants || 0;
               setMaxCapacity(eventMaxCapacity);
            }
         } catch (error) {
            console.error('Error fetching approved attendees count:', error);
            // Fallback to event members if available
            if (event?.members) {
               const approvedMembers = event.members.filter((m: any) => m.status === 'accepted' || m.status === 'approved');
               setApprovedAttendeesCount(approvedMembers.length);
            } else {
               setApprovedAttendeesCount(0);
            }
            // Get maxCapacity from event data
            const eventMaxCapacity = (event as any)?.maxCapacity || (event as any)?.capacity || (event as any)?.maxParticipants || 0;
            setMaxCapacity(eventMaxCapacity);
         }
      };

      if (event) {
         fetchApprovedCount();
      }
   }, [eventId, event]);

   // Record event view
   useEffect(() => {
      const recordView = async () => {
         if (!eventId || !event) return;

         try {
            // Determine source from URL params
            const urlParams = new URLSearchParams(window.location.search);
            const source = urlParams.get('source') || 'shareable_link'; // Can be 'qr_scan' or 'shareable_link'

            // Check if view was already recorded in this session for this event+source
            // Using sessionStorage (like business card tracking) to prevent duplicate tracking on refresh
            const sessionKey = `event_view_recorded_${eventId}_${source}`;
            const alreadyRecordedInSession = sessionStorage.getItem(sessionKey);

            if (!viewRecorded && !alreadyRecordedInSession) {
               // Generate fingerprint for tracking
               const fingerprint = await generateFingerprint(true); // true = hardware-only mode
               console.log('Recording event view with fingerprint:', fingerprint);
               console.log('Event view source:', source);

               // Prepare viewer info (profile avatar lives in userProfiles in Firestore)
               let viewerInfo: {
                  viewerId: string | null;
                  viewerName: string;
                  viewerEmail: string | null;
                  viewerPhotoURL: string | null;
                  userAgent: string;
               };
               if (currentUser) {
                  let viewerPhotoURL: string | null = currentUser.photoURL || null;
                  let viewerName = currentUser.displayName || 'Unknown User';
                  try {
                     const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
                     if (profileDoc.exists()) {
                        const profile = profileDoc.data();
                        viewerPhotoURL =
                           profile.photoURL ||
                           profile.profilePicture ||
                           profile.avatar ||
                           profile.profilePhoto ||
                           viewerPhotoURL;
                        viewerName =
                           profile.displayName ||
                           profile.fullName ||
                           profile.firstName ||
                           viewerName;
                     }
                  } catch (_) {
                     // Keep auth fallbacks if fetch fails
                  }
                  viewerInfo = {
                     viewerId: currentUser.uid,
                     viewerName,
                     viewerEmail: currentUser.email || null,
                     viewerPhotoURL,
                     userAgent: navigator.userAgent,
                  };
               } else {
                  viewerInfo = {
                     viewerId: null,
                     viewerName: 'Anonymous',
                     viewerEmail: null,
                     viewerPhotoURL: null,
                     userAgent: navigator.userAgent,
                  };
               }

               // Record view via API
               const response = await fetch(`/api/events/${eventId}/record-view`, {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                     fingerprint,
                     viewerInfo,
                     source,
                     ...getPublicViewAnalyticsPayload(),
                  }),
               });

               if (response.ok) {
                  const result = await response.json();
                  console.log('Event view recorded successfully:', result);
                  setViewRecorded(true);
                  // Mark as recorded in this session to prevent duplicate views on refresh
                  sessionStorage.setItem(sessionKey, 'true');
                  console.log('✅ View recorded and marked in session storage');
               }
            } else if (alreadyRecordedInSession) {
               console.log('ℹ️ View already recorded in this session, skipping duplicate');
            }
         } catch (error) {
            console.error('Error recording event view:', error);
         }
      };

      recordView();
   }, [eventId, event, currentUser, viewRecorded]);

   const handleRSVPRequest = async () => {
      // Check if user is already logged in
      if (currentUser) {
         // User is logged in, send RSVP request directly
         setSubmitting(true);
         try {
            const token = await auth.currentUser?.getIdToken();
            
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/request-rsvp`,
               {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                     'Authorization': `Bearer ${token}`,
                  },
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('RSVP request sent successfully! You will be notified when approved.');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
            } else {
               setSnackbarMessage(result.error || 'Failed to send RSVP request');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (error) {
            console.error('Error sending RSVP request:', error);
            setSnackbarMessage('Failed to send RSVP request. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            setSubmitting(false);
         }
      } else {
         // User is not logged in, open signup modal
         setSignupModalOpen(true);
      }
   };

   const handleRSVPFormSubmit = async () => {
      // Validate required fields
      if (!rsvpFirstName.trim() || !rsvpLastName.trim() || !email.trim() || !rsvpRole.trim()) {
         setSnackbarMessage('Please fill in all required fields (First Name, Last Name, Email, Role)');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      // If user is not logged in, validate password
      if (!currentUser) {
         if (!rsvpPassword.trim()) {
            setSnackbarMessage('Please enter a password');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }
         if (!userExists && rsvpPassword !== rsvpConfirmPassword) {
            setSnackbarMessage('Passwords do not match');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }
      }

      // Check if user is already logged in
      if (currentUser) {
         // User is logged in, send RSVP request with form data
         setSubmitting(true);
         try {
            const token = await auth.currentUser?.getIdToken();
            
            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/events/${eventId}/request-rsvp`,
               {
                  method: 'POST',
                  headers: {
                     'Content-Type': 'application/json',
                     'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                     firstName: rsvpFirstName,
                     lastName: rsvpLastName,
                     role: rsvpRole,
                     message: rsvpMessage,
                     city: rsvpCity,
                  }),
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('RSVP request sent successfully! You will be notified when approved.');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               // Reset form
               setRsvpFirstName('');
               setRsvpLastName('');
               setRsvpRole('');
               setRsvpMessage('');
               setRsvpCity('');
               setEmail('');
               // Redirect to dashboard after a short delay
               setTimeout(() => {
                  navigate('/dashboard');
               }, 1500);
            } else {
               setSnackbarMessage(result.error || 'Failed to send RSVP request');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (error) {
            console.error('Error sending RSVP request:', error);
            setSnackbarMessage('Failed to send RSVP request. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            setSubmitting(false);
         }
      } else {
         // User is not logged in, submit directly using registration/login logic
         setSubmitting(true);
         try {
            const fullName = `${rsvpFirstName} ${rsvpLastName}`.trim();
            
            // Submit directly using public-rsvp endpoint with all form data
            // Use the same logic as handleRegistration
            const submitResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/events/public-rsvp`, {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  eventId,
                  email,
                  password: rsvpPassword,
                  name: fullName,
                  firstName: rsvpFirstName,
                  lastName: rsvpLastName,
                  role: rsvpRole,
                  message: rsvpMessage,
                  city: rsvpCity,
                  action: userExists ? 'login' : 'register',
               }),
            });

            const submitResult = await submitResponse.json();

            if (submitResult.success) {
               setSnackbarMessage(
                  'Request submitted successfully! You will be notified when your request is approved.'
               );
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               // Reset form
               setRsvpFirstName('');
               setRsvpLastName('');
               setRsvpRole('');
               setRsvpMessage('');
               setRsvpCity('');
               setRsvpPassword('');
               setRsvpConfirmPassword('');
               setEmail('');
               setUserExists(false);
               // Redirect to dashboard after a short delay
               setTimeout(() => {
                  navigate('/dashboard');
               }, 1500);
            } else {
               setSnackbarMessage(submitResult.error || 'Registration failed');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (err) {
            console.error('Error processing RSVP:', err);
            setSnackbarMessage('Failed to process request. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            setSubmitting(false);
         }
      }
   };

   const handleSignupSuccess = async (user: any) => {
      try {
         console.log('✅ User signed up successfully:', user);
         console.log('✅ User automatically added to event waitlist by registration endpoint');

         // Record signup through event analytics
         await fetch(`/api/events/${eventId}/record-signup`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               userId: user.uid,
               userInfo: {
                  userName: user.displayName || user.fullName || 'Unknown',
                  userEmail: user.email || null,
                  userPhotoURL: user.photoURL || null,
               },
            }),
         });

         setSnackbarMessage(
            'Account created successfully! Your request to join the event has been submitted and is pending approval.'
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setSignupModalOpen(false);
      } catch (error) {
         console.error('Error recording signup analytics:', error);
         // Still show success since user is registered and on waitlist
         setSnackbarMessage(
            'Account created successfully! Your request to join the event has been submitted and is pending approval.'
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setSignupModalOpen(false);
      }
   };

   const handleRSVPSubmit = async () => {
      if (!email.trim()) {
         setSnackbarMessage('Please enter your email address');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Check if user exists
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email }),
            }
         );

         const result = await response.json();

         if (result.exists) {
            // User exists, show login modal
            setRsvpModalOpen(false);
            setRegistrationModalOpen(true);
         } else {
            // User doesn't exist, show registration modal
            setRsvpModalOpen(false);
            setRegistrationModalOpen(true);
         }
      } catch (err) {
         console.error('Error checking user:', err);
         setSnackbarMessage('Failed to process request. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleRegistration = async () => {
      if (!email.trim() || !password.trim() || !name.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Register user and add to waiting list
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/events/public-rsvp`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               eventId,
               email,
               password,
               name,
               action: 'register',
            }),
         });

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage(
               'Request submitted successfully! You will be notified when your request is approved.'
            );
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setRegistrationModalOpen(false);
         } else {
            setSnackbarMessage(result.error || 'Registration failed');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error registering:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleLogin = async () => {
      if (!email.trim() || !password.trim()) {
         setSnackbarMessage('Please enter your email and password');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         // Login user and add to waiting list
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/events/public-rsvp`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               eventId,
               email,
               password,
               action: 'login',
            }),
         });

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage(
               'Login successful! You will be notified when your request is approved.'
            );
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setRegistrationModalOpen(false);
         } else {
            setSnackbarMessage(result.error || 'Login failed');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error logging in:', err);
         setSnackbarMessage('Login failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const formatEventDate = (date: any) => {
      if (!date) return 'TBD';
      const eventDate = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return format(eventDate, 'EEEE, MMMM do, yyyy');
   };

   const formatEventTime = (startDate: any, endDate: any) => {
      if (!startDate) return 'Time TBD';

      const start = startDate.seconds ? new Date(startDate.seconds * 1000) : new Date(startDate);
      const startTime = format(start, 'h:mm a');

      if (!endDate) return startTime;

      const end = endDate.seconds ? new Date(endDate.seconds * 1000) : new Date(endDate);
      const endTime = format(end, 'h:mm a');

      return `${startTime} - ${endTime}`;
   };

   const getLocationDisplay = () => {
      if (!event?.location) return 'Location TBD';
      if (typeof event.location === 'string') return event.location;
      if (event.isVirtual) return 'Virtual Event';
      return event.location.name || event.location.address || 'Location TBD';
   };

   // Check if user has access (for conditional rendering only)
   useEffect(() => {
      if (!event || !currentUser) {
         setHasAccess(false);
         return;
      }

      // Check if user is in participants/members with accepted status
      const participants = event.participants || [];
      const members = event.members || [];
      
      const userParticipant = participants.find(
         (p: any) => (p.userId === currentUser.uid || p.email === currentUser.email) && 
         (p.status === 'accepted' || !p.status || p.role === 'owner' || p.role === 'host')
      );
      
      const userMember = members.find(
         (m: any) => (m.userId === currentUser.uid || m.email === currentUser.email) && 
         (m.status === 'accepted' || !m.status || m.role === 'owner' || m.role === 'host')
      );

      // Also check if user is event owner
      const isOwner = event.userId === currentUser.uid || (event as any).createdBy === currentUser.uid;

      setHasAccess(!!(userParticipant || userMember || isOwner));
   }, [event, currentUser]);

   // Event countdown timer
   useEffect(() => {
      if (!event?.startDate) {
         setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
         return;
      }

      const updateCountdown = () => {
         // Handle both Firestore Timestamp and regular Date objects
         let startDate: Date;
         const startDateValue = event.startDate as any;
         
         if (startDateValue?.seconds) {
            // Firestore Timestamp with seconds property
            startDate = new Date(startDateValue.seconds * 1000);
         } else if (startDateValue?.toDate && typeof startDateValue.toDate === 'function') {
            // Firestore Timestamp with toDate method
            startDate = startDateValue.toDate();
         } else if (startDateValue instanceof Date) {
            // Regular Date object
            startDate = startDateValue;
         } else {
            // Try to convert to Date
            startDate = new Date(startDateValue);
         }

         const now = new Date();
         const diff = startDate.getTime() - now.getTime();

         if (diff <= 0) {
            setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            if (countdownIntervalRef.current) {
               clearInterval(countdownIntervalRef.current);
               countdownIntervalRef.current = null;
            }
            return;
         }

         const days = Math.floor(diff / (1000 * 60 * 60 * 24));
         const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
         const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
         const seconds = Math.floor((diff % (1000 * 60)) / 1000);

         setCountdown({ days, hours, minutes, seconds });
      };

      // Initial update
      updateCountdown();
      
      // Set up interval
      countdownIntervalRef.current = setInterval(updateCountdown, 1000);

      // Cleanup on unmount or when event changes
      return () => {
         if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
         }
      };

      return () => {
         if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
         }
      };
   }, [event?.startDate]);

   // Generate QR code
   useEffect(() => {
      if (!eventId) return;

      const generateQR = async () => {
         try {
            const eventUrl = `${window.location.origin}/events/public/${eventId}`;
            const qrDataURL = await QRCode.toDataURL(eventUrl, {
               width: 200,
               margin: 2,
               color: {
                  dark: '#000000',
                  light: '#ffffff',
               },
            });
            setQrCodeDataUrl(qrDataURL);
         } catch (error) {
            console.error('Error generating QR code:', error);
         }
      };

      generateQR();
   }, [eventId]);

   // Copy event link handler
   const handleCopyLink = async () => {
      try {
         const eventUrl = `${window.location.origin}/events/public/${eventId}`;
         await navigator.clipboard.writeText(eventUrl);
         setCopiedLink(true);
         setTimeout(() => setCopiedLink(false), 2000);
      } catch (error) {
         console.error('Error copying link:', error);
         setSnackbarMessage('Failed to copy link');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   // Get visible participants (display filtering only)
   const getVisibleParticipants = () => {
      if (!event || participants.length === 0) return [];
      
      // Show all participants - no filtering by access
      // Filter out only rejected/declined participants
      return participants.filter(
         (p: any) => p.status !== 'rejected' && p.status !== 'declined'
      );
   };


   if (loading) {
      return (
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'center',
               alignItems: 'center',
               minHeight: '100vh',
               bgcolor: 'background.default',
            }}
         >
            <CircularProgress />
         </Box>
      );
   }

   if (error || !event) {
      return (
         <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Alert severity='error' sx={{ textAlign: 'center' }}>
               {error || 'Event not found'}
            </Alert>
         </Container>
         </Box>
      );
   }

   const visibleParticipants = getVisibleParticipants();
   const eventUrl = `${window.location.origin}/events/public/${eventId}`;
   const attendeeCount = visibleParticipants.length;
   
   // Calculate capacity percentage
   const capacityPercentage = maxCapacity > 0 
      ? Math.min(100, Math.round((approvedAttendeesCount / maxCapacity) * 100))
      : 0;

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
         {/* Top Navigation Bar */}
         <AppBar
            position='sticky'
                     sx={{
               bgcolor: 'rgba(8, 10, 15, 0.90)',
               backdropFilter: 'blur(20px)',
               borderBottom: '1px solid',
               borderColor: 'divider',
               boxShadow: 'none',
            }}
         >
            <Toolbar sx={{ px: { xs: 2, md: 5 }, justifyContent: 'space-between' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
                  <Typography
                     sx={{
                        fontFamily: FONT_DISPLAY,
                        fontSize: 18,
                        fontWeight: 600,
                        color: 'text.primary',
                     }}
                  >
                     WeTrippen
                  </Typography>
               </Box>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Button
                     variant='outlined'
                     sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.13)',
                        color: 'text.secondary',
                        px: 2.25,
                        py: 0.875,
                        borderRadius: 1,
                        fontSize: 13,
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                           borderColor: 'rgba(255, 255, 255, 0.2)',
                           color: 'text.primary',
                        },
                     }}
                     onClick={() => navigate('/login')}
                  >
                     Sign in
                  </Button>
                           <Button
                              variant='contained'
                              sx={{
                        bgcolor: 'primary.main',
                        color: '#000',
                        px: 2.25,
                        py: 0.875,
                        borderRadius: 1,
                        fontSize: 13,
                        fontWeight: 700,
                        textTransform: 'none',
                                 '&:hover': {
                           bgcolor: '#00DFC0',
                                 },
                              }}
                     onClick={handleRSVPRequest}
                           >
                     Request Access →
                           </Button>
               </Box>
            </Toolbar>
         </AppBar>

         {/* Hero Section */}
         <Box
                                 sx={{
               position: 'relative',
               minHeight: { xs: 'auto', md: 520 },
               display: 'flex',
               alignItems: 'flex-end',
               overflow: 'hidden',
               background: `
                  radial-gradient(ellipse 70% 70% at 15% 30%, rgba(0,196,167,0.13) 0%, transparent 55%),
                  radial-gradient(ellipse 50% 60% at 85% 70%, rgba(91,138,245,0.07) 0%, transparent 55%),
                  linear-gradient(160deg, #0d1821 0%, #080A0F 60%)
               `,
            }}
         >
            {/* Grid overlay */}
            <Box
               sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `
                     repeating-linear-gradient(0deg, transparent, transparent 59px, rgba(255,255,255,0.018) 60px),
                     repeating-linear-gradient(90deg, transparent, transparent 59px, rgba(255,255,255,0.018) 60px)
                  `,
                  pointerEvents: 'none',
               }}
            />
            {/* Fade bottom */}
            <Box
               sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 220,
                  background: 'linear-gradient(to top, #080A0F 0%, transparent 100%)',
                  pointerEvents: 'none',
               }}
            />
            <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 2, py: { xs: 4, md: 12.5 }, px: { xs: 2, md: 5 } }}>
               <Grid container spacing={7} alignItems='flex-end'>
                  {/* Left Column - Event Info */}
                  <Grid item xs={12} md={8}>
                     {/* Event Category Chip */}
                     {event.eventType && (
                        <Chip
                           label={event.eventType.toUpperCase()}
                           icon={
                              <Box
                                 sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                 }}
                              />
                           }
                           sx={{
                              fontFamily: FONT_MONO,
                              fontSize: 9,
                              letterSpacing: '3px',
                              textTransform: 'uppercase',
                              bgcolor: 'rgba(0, 196, 167, 0.10)',
                              border: '1px solid rgba(0, 196, 167, 0.22)',
                              color: 'primary.main',
                              px: 1.625,
                              py: 0.625,
                              mb: 2.75,
                              height: 'auto',
                              '& .MuiChip-icon': {
                                 marginLeft: 0.875,
                                 marginRight: -0.5,
                              },
                           }}
                        />
                     )}

                     {/* Event Title */}
                     <Typography
                        variant='h1'
                        sx={{
                           fontFamily: FONT_DISPLAY,
                           fontSize: { xs: '2.5rem', md: '4.25rem' },
                           fontWeight: 300,
                           letterSpacing: '-0.025em',
                           lineHeight: 0.93,
                           color: 'text.primary',
                           mb: 1,
                        }}
                     >
                        {event.title}
                     </Typography>

                     {/* Host Information */}
                     {eventHost && (() => {
                        const hostPhotoUrl =
                           eventHost?.photoURL ||
                           eventHost?.profileData?.photoURL ||
                           eventHost?.profileData?.profilePicture ||
                           eventHost?.profileData?.avatar;
                        return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, my: 2.5 }}>
                           <Avatar
                              src={
                                 hostPhotoUrl ||
                                 (eventHost?.displayName
                                    ? `https://ui-avatars.com/api/?name=${encodeURIComponent(eventHost.displayName)}&background=00C4A7&color=000000&size=256`
                                    : undefined)
                              }
                              sx={{
                                 width: 32,
                                 height: 32,
                                 bgcolor: hostPhotoUrl ? 'transparent' : 'primary.main',
                                 color: hostPhotoUrl ? 'inherit' : '#000',
                                 fontSize: 13,
                                 fontWeight: 700,
                              }}
                           >
                              {!hostPhotoUrl && (eventHost?.displayName?.charAt(0)?.toUpperCase() || 'E')}
                           </Avatar>
                           <Box>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    letterSpacing: '1.5px',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                    display: 'block',
                                    mb: 0.125,
                                 }}
                              >
                                 Hosted by
                              </Typography>
                              <Typography
                                 sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.80)',
                                 }}
                              >
                                 {eventHost.displayName || 'Event Host'}
                              </Typography>
                           </Box>
                        </Box>
                        );
                     })()}

                     {/* Meta Chips Row */}
                     <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                        {event.startDate && (
                           <Chip
                              icon={<DateRangeIcon sx={{ fontSize: 13 }} />}
                              label={
                                 <Typography component='span' sx={{ fontWeight: 600 }}>
                                    {formatEventDate(event.startDate)}
                                 </Typography>
                              }
                              sx={{
                                 bgcolor: 'rgba(255, 255, 255, 0.05)',
                                 border: '1px solid rgba(255, 255, 255, 0.13)',
                                 borderRadius: 1.25,
                                 px: 1.875,
                                 py: 1.125,
                                 fontSize: 13,
                                 color: 'text.secondary',
                                 '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                 },
                                 '& .MuiChip-icon': {
                                    color: 'text.secondary',
                                 },
                              }}
                           />
                        )}
                        {event.startDate && (
                           <Chip
                              icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
                              label={
                                 <Typography component='span' sx={{ fontWeight: 600 }}>
                                    {formatEventTime(event.startDate, event.endDate)}
                                 </Typography>
                              }
                              sx={{
                                 bgcolor: 'rgba(255, 255, 255, 0.05)',
                                 border: '1px solid rgba(255, 255, 255, 0.13)',
                                 borderRadius: 1.25,
                                 px: 1.875,
                                 py: 1.125,
                                 fontSize: 13,
                                 color: 'text.secondary',
                                 '&:hover': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                 },
                                 '& .MuiChip-icon': {
                                    color: 'text.secondary',
                                 },
                              }}
                           />
                        )}
                        <Chip
                           icon={<PlaceIcon sx={{ fontSize: 13 }} />}
                           label={
                              <Typography component='span' sx={{ fontWeight: 600 }}>
                                 {getLocationDisplay()}
                              </Typography>
                           }
                           sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.13)',
                              borderRadius: 1.25,
                              px: 1.875,
                              py: 1.125,
                              fontSize: 13,
                              color: 'text.secondary',
                              '&:hover': {
                                 borderColor: 'rgba(255, 255, 255, 0.2)',
                              },
                              '& .MuiChip-icon': {
                                 color: 'text.secondary',
                              },
                           }}
                        />
                        <Chip
                           icon={<PublicIcon sx={{ fontSize: 13 }} />}
                           label={
                              <Typography component='span' sx={{ fontWeight: 600 }}>
                                 Public Event
                              </Typography>
                           }
                           sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.13)',
                              borderRadius: 1.25,
                              px: 1.875,
                              py: 1.125,
                              fontSize: 13,
                              color: 'text.secondary',
                              '&:hover': {
                                 borderColor: 'rgba(255, 255, 255, 0.2)',
                              },
                              '& .MuiChip-icon': {
                                 color: 'text.secondary',
                              },
                           }}
                        />
                     </Box>

                     {/* Action Buttons */}
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
                        <Button
                           variant='contained'
                           onClick={() => {
                              setActiveTab(2); // Switch to RSVP tab
                              if (!currentUser) {
                                 handleRSVPRequest();
                              }
                           }}
                           disabled={submitting}
                           startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : <PersonAddIcon />}
                           sx={{
                              bgcolor: 'primary.main',
                              color: '#000',
                              px: 3.75,
                              py: 1.75,
                              borderRadius: 1.25,
                              fontSize: 14,
                              fontWeight: 700,
                              textTransform: 'none',
                              boxShadow: '0 8px 28px rgba(0,196,167,0.28)',
                              '&:hover': {
                                 bgcolor: '#00DFC0',
                                 transform: 'translateY(-1px)',
                                 boxShadow: '0 12px 36px rgba(0,196,167,0.38)',
                              },
                           }}
                        >
                           {submitting ? 'Sending...' : 'Request RSVP →'}
                        </Button>
                        <Button
                           variant='outlined'
                           onClick={handleCopyLink}
                           startIcon={copiedLink ? <CheckIcon /> : <ShareIcon />}
                           sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.06)',
                              borderColor: 'rgba(255, 255, 255, 0.13)',
                              color: 'text.secondary',
                              px: 2.75,
                              py: 1.75,
                              borderRadius: 1.25,
                              fontSize: 14,
                              fontWeight: 500,
                              textTransform: 'none',
                              '&:hover': {
                                 color: 'text.primary',
                                 borderColor: 'rgba(255, 255, 255, 0.25)',
                              },
                           }}
                        >
                           {copiedLink ? 'Copied!' : 'Share Event'}
                        </Button>
                     </Box>

                     {/* Waitlist Social Proof */}
                     {attendeeCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2.5 }}>
                           <Box sx={{ display: 'flex' }}>
                              {visibleParticipants.slice(0, 4).map((p: any, idx: number) => {
                                 const photoUrl =
                                    p.photoURL ||
                                    p.profileData?.photoURL ||
                                    p.profileData?.profilePicture ||
                                    p.profileData?.avatar ||
                                    p.profileData?.profilePhoto;
                                 return (
                                    <Avatar
                                       key={idx}
                                       src={photoUrl || undefined}
                                       sx={{
                                          width: 26,
                                          height: 26,
                                          border: '2px solid',
                                          borderColor: 'background.default',
                                          ml: idx > 0 ? -1.75 : 0,
                                          bgcolor: photoUrl ? 'transparent' : 'primary.main',
                                          color: photoUrl ? 'inherit' : '#000',
                                          fontSize: 10,
                                          fontWeight: 700,
                                       }}
                                    >
                                       {!photoUrl && (p.displayName?.charAt(0)?.toUpperCase() || 'U')}
                                    </Avatar>
                                 );
                              })}
                           </Box>
                           <Typography
                              sx={{
                                 fontSize: 12,
                                 color: 'rgba(255, 255, 255, 0.35)',
                                 '& strong': {
                                    color: 'rgba(255, 255, 255, 0.60)',
                                 },
                              }}
                           >
                              <strong>{attendeeCount} people</strong> on the waitlist · Spots filling fast
                                 </Typography>
                              </Box>
                     )}
                           </Grid>

                  {/* Right Column - QR + Stats */}
                  <Grid item xs={12} md={4}>
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {/* QR Code Card */}
                        <Paper
                           sx={{
                              bgcolor: 'rgba(14, 17, 24, 0.80)',
                              backdropFilter: 'blur(12px)',
                              border: '1px solid',
                              borderColor: 'rgba(255, 255, 255, 0.13)',
                              borderRadius: 2.25,
                              p: 2.75,
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 1,
                                 mb: 2,
                              }}
                           >
                                 <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    letterSpacing: '2.5px',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                 }}
                              >
                                 Scan to Access
                                 </Typography>
                              <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(0, 196, 167, 0.10)' }} />
                              </Box>
                           {qrCodeDataUrl && (
                              <Box
                                 sx={{
                                    bgcolor: 'white',
                                    borderRadius: 1.5,
                                    p: 1.75,
                                    mb: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                              >
                                 <img src={qrCodeDataUrl} alt='QR Code' style={{ width: '100%', maxWidth: 150 }} />
                              </Box>
                           )}
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 10,
                                 color: 'primary.main',
                                 wordBreak: 'break-all',
                                 textAlign: 'center',
                                 lineHeight: 1.6,
                              }}
                           >
                              {eventUrl.replace('https://', '').replace('http://', '')}
                           </Typography>
                        </Paper>

                        {/* Capacity Card */}
                        <Paper
                           sx={{
                              bgcolor: 'rgba(14, 17, 24, 0.80)',
                              backdropFilter: 'blur(12px)',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.75,
                              p: 2,
                           }}
                        >
                           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                 }}
                              >
                                 Capacity
                              </Typography>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_DISPLAY,
                                    fontSize: 20,
                                    fontWeight: 300,
                                    color: 'primary.main',
                                 }}
                              >
                                 {maxCapacity > 0 ? `${capacityPercentage}%` : `${approvedAttendeesCount}`}
                              </Typography>
                           </Box>
                           <LinearProgress
                              variant='determinate'
                              value={capacityPercentage}
                              sx={{
                                 height: 5,
                                 borderRadius: '999px',
                                 bgcolor: 'rgba(255, 255, 255, 0.07)',
                                 '& .MuiLinearProgress-bar': {
                                    borderRadius: '999px',
                                    background: 'linear-gradient(to right, #00C4A7, #00E8C8)',
                                 },
                              }}
                           />
                        </Paper>

                        {/* Statistics Cards */}
                        <Grid container spacing={1}>
                           <Grid item xs={6}>
                              <Paper
                                 sx={{
                                    bgcolor: 'rgba(14, 17, 24, 0.80)',
                                    backdropFilter: 'blur(12px)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.75,
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                 }}
                              >
                                 <Box>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_DISPLAY,
                                          fontSize: 30,
                                          fontWeight: 300,
                                          color: 'primary.main',
                                          lineHeight: 1,
                                       }}
                                    >
                                       {albumCount}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '1.5px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                          mt: 0.375,
                                       }}
                                    >
                                       Albums
                                    </Typography>
                                 </Box>
                                 <FolderIcon sx={{ fontSize: 20, opacity: 0.25 }} />
                              </Paper>
                           </Grid>
                           <Grid item xs={6}>
                              <Paper
                                 sx={{
                                    bgcolor: 'rgba(14, 17, 24, 0.80)',
                                    backdropFilter: 'blur(12px)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.75,
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                 }}
                              >
                                 <Box>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_DISPLAY,
                                          fontSize: 30,
                                          fontWeight: 300,
                                          color: 'primary.main',
                                          lineHeight: 1,
                                       }}
                                    >
                                       {approvedAttendeesCount > 0 ? approvedAttendeesCount : attendeeCount}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '1.5px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                          mt: 0.375,
                                       }}
                                    >
                                       Attending
                                 </Typography>
                              </Box>
                                 <PeopleIcon sx={{ fontSize: 20, opacity: 0.25 }} />
                              </Paper>
                           </Grid>
                        </Grid>
                     </Box>
                  </Grid>
               </Grid>
            </Container>
         </Box>

         {/* Main Content Area */}
         <Container maxWidth='lg' sx={{ py: { xs: 4, md: 7 }, px: { xs: 2, md: 5 } }}>
            <Grid container spacing={6} alignItems='flex-start'>
               {/* Left Column - Tabs */}
               <Grid item xs={12} md={8}>
                  {/* Tab Navigation */}
                  <Tabs
                     value={activeTab}
                     onChange={(_, newValue) => setActiveTab(newValue)}
                     sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        mb: 4.5,
                        '& .MuiTab-root': {
                           fontFamily: FONT_BODY,
                           fontSize: 13,
                           fontWeight: 500,
                           color: 'rgba(255, 255, 255, 0.35)',
                           px: 2.5,
                           py: 1.625,
                           textTransform: 'none',
                           minHeight: 'auto',
                           '&.Mui-selected': {
                              color: 'primary.main',
                              fontWeight: 600,
                           },
                           '&:hover:not(.Mui-selected)': {
                              color: 'rgba(255, 255, 255, 0.60)',
                           },
                        },
                        '& .MuiTabs-indicator': {
                           bgcolor: 'primary.main',
                        },
                     }}
                  >
                     <Tab label='Details' />
                     <Tab label='Participants' />
                     <Tab label='Request RSVP' />
                  </Tabs>

                  {/* Tab Panels */}
                  {/* Details Tab */}
                  {activeTab === 0 && (
                     <Box>
                        {/* About Section */}
                        {event.description && (
                           <Box sx={{ mb: 4.5 }}>
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    mb: 1.75,
                                 }}
                              >
                              <Typography
                                    sx={{
                                       fontFamily: FONT_MONO,
                                       fontSize: 9,
                                       letterSpacing: '3px',
                                       textTransform: 'uppercase',
                                       color: 'primary.main',
                                    }}
                                 >
                                    About this event
                              </Typography>
                                 <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(0, 196, 167, 0.10)' }} />
                              </Box>
                              <Typography
                                 variant='h2'
                                 sx={{
                                    fontFamily: FONT_DISPLAY,
                                    fontSize: 28,
                                    fontWeight: 300,
                                    letterSpacing: '-0.005em',
                                    color: 'text.primary',
                                    mb: 1.75,
                                 }}
                              >
                                 {event.title}
                              </Typography>
                              <Typography
                                 sx={{
                                    fontSize: 15,
                                    color: 'text.secondary',
                                    lineHeight: 1.85,
                                    maxWidth: 540,
                                 }}
                                 dangerouslySetInnerHTML={{ __html: event.description }}
                              />
                           </Box>
                        )}

                        {/* Event Details List */}
                              <Box
                                 sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                              mb: 1.75,
                           }}
                        >
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 9,
                                 letterSpacing: '3px',
                                 textTransform: 'uppercase',
                                 color: 'primary.main',
                              }}
                           >
                              Event Details
                           </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                           {event.startDate && (
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    py: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                 }}
                              >
                                 <Box
                                    sx={{
                                       width: 36,
                                       height: 36,
                                       borderRadius: 1.25,
                                       bgcolor: 'background.paper',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       fontSize: 16,
                                       flexShrink: 0,
                                    }}
                                 >
                                    📅
                                 </Box>
                                 <Box>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '1.5px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                          mb: 0.375,
                                       }}
                                    >
                                       Date
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontSize: 14,
                                          color: 'rgba(255, 255, 255, 0.80)',
                                          fontWeight: 500,
                                       }}
                                    >
                                       {formatEventDate(event.startDate)}
                                    </Typography>
                                 </Box>
                              </Box>
                           )}
                           {event.startDate && (
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    py: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                 }}
                              >
                                 <Box
                                    sx={{
                                       width: 36,
                                       height: 36,
                                       borderRadius: 1.25,
                                       bgcolor: 'background.paper',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       fontSize: 16,
                                       flexShrink: 0,
                                    }}
                                 >
                                    🕐
                                 </Box>
                                 <Box>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '1.5px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                          mb: 0.375,
                                       }}
                                    >
                                       Time
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontSize: 14,
                                          color: 'rgba(255, 255, 255, 0.80)',
                                          fontWeight: 500,
                                       }}
                                    >
                                       {formatEventTime(event.startDate, event.endDate)}
                                    </Typography>
                                 </Box>
                              </Box>
                           )}
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'flex-start',
                                 gap: 2,
                                 py: 2,
                                 borderBottom: '1px solid',
                                 borderColor: 'divider',
                              }}
                           >
                              <Box
                                 sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1.25,
                                    bgcolor: 'background.elevated',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 16,
                                    flexShrink: 0,
                                 }}
                              >
                                 📍
                              </Box>
                              <Box>
                                 <Typography
                                    sx={{
                                       fontFamily: FONT_MONO,
                                       fontSize: 9,
                                       letterSpacing: '1.5px',
                                       textTransform: 'uppercase',
                                       color: 'rgba(255, 255, 255, 0.35)',
                                       mb: 0.375,
                                    }}
                                 >
                                    Location
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: 14,
                                       color: 'rgba(255, 255, 255, 0.80)',
                                       fontWeight: 500,
                                    }}
                                 >
                                    {getLocationDisplay()}
                                 </Typography>
                              </Box>
                           </Box>
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'flex-start',
                                 gap: 2,
                                 py: 2,
                                 borderBottom: '1px solid',
                                 borderColor: 'divider',
                              }}
                           >
                              <Box
                                 sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1.25,
                                    bgcolor: 'background.elevated',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 16,
                                    flexShrink: 0,
                                 }}
                              >
                                 🌐
                              </Box>
                              <Box>
                                 <Typography
                                    sx={{
                                       fontFamily: FONT_MONO,
                                       fontSize: 9,
                                       letterSpacing: '1.5px',
                                       textTransform: 'uppercase',
                                       color: 'rgba(255, 255, 255, 0.35)',
                                       mb: 0.375,
                                    }}
                                 >
                                    Visibility
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: 14,
                                       color: 'rgba(255, 255, 255, 0.80)',
                                       fontWeight: 500,
                                    }}
                                 >
                                    Public Event
                                 </Typography>
                                 <Typography
                                    sx={{
                                       fontSize: 12,
                                       color: 'rgba(255, 255, 255, 0.35)',
                                       fontWeight: 400,
                                       mt: 0.125,
                                    }}
                                 >
                                    Anyone with the link can request RSVP
                                 </Typography>
                              </Box>
                           </Box>
                        </Box>
                     </Box>
                  )}

                  {/* Participants Tab */}
                  {activeTab === 1 && (
                     <Box>
                        <Typography
                           sx={{
                              fontSize: 14,
                              color: 'rgba(255, 255, 255, 0.35)',
                              mb: 3,
                              lineHeight: 1.7,
                           }}
                        >
                           Connect with others attending this event.{' '}
                           {!hasAccess && 'Request RSVP to see the full participant list.'}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                           {visibleParticipants.map((participant: any, idx: number) => (
                              <Paper
                                 key={idx}
                                 sx={{
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    p: 1.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    ...(participant.role === 'owner' || participant.role === 'host'
                                       ? {
                                            borderLeft: '3px solid',
                                            borderLeftColor: 'primary.main',
                                         }
                                       : {}),
                                    '&:hover': {
                                       borderColor: 'rgba(255, 255, 255, 0.13)',
                                    },
                                 }}
                              >
                                 <Avatar
                                    src={
                                       (() => {
                                          const photoUrl =
                                             participant.photoURL ||
                                             participant.profileData?.photoURL ||
                                             participant.profileData?.profilePicture ||
                                             participant.profileData?.avatar ||
                                             participant.profileData?.profilePhoto;
                                          if (photoUrl) return photoUrl;
                                          if (participant.displayName || participant.name)
                                             return `https://ui-avatars.com/api/?name=${encodeURIComponent(participant.displayName || participant.name || 'User')}&background=00C4A7&color=000000&size=256`;
                                          return undefined;
                                       })()
                                    }
                                    sx={{
                                       width: 38,
                                       height: 38,
                                       bgcolor:
                                          participant.photoURL ||
                                          participant.profileData?.photoURL ||
                                          participant.profileData?.profilePicture ||
                                          participant.profileData?.avatar
                                             ? 'transparent'
                                             : 'primary.main',
                                       color:
                                          participant.photoURL ||
                                          participant.profileData?.photoURL ||
                                          participant.profileData?.profilePicture ||
                                          participant.profileData?.avatar
                                             ? 'inherit'
                                             : '#000',
                                       fontSize: 14,
                                       fontWeight: 700,
                                    }}
                                 >
                                    {!(
                                       participant.photoURL ||
                                       participant.profileData?.photoURL ||
                                       participant.profileData?.profilePicture ||
                                       participant.profileData?.avatar
                                    ) &&
                                       (participant.displayName?.charAt(0)?.toUpperCase() ||
                                          participant.name?.charAt(0)?.toUpperCase() ||
                                          'U')}
                                 </Avatar>
                                 <Box sx={{ flex: 1 }}>
                                    <Typography
                                       sx={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          color: 'text.primary',
                                          mb: 0.125,
                                       }}
                                    >
                                       {participant.displayName || participant.name || 'Unknown User'}
                                    </Typography>
                                    {(participant.role === 'owner' || participant.role === 'host' || event.userId === participant.userId || (event as any).createdBy === participant.userId) && (
                                       <Typography
                                          sx={{
                                             fontFamily: FONT_MONO,
                                             fontSize: 9,
                                             letterSpacing: '1px',
                                             textTransform: 'uppercase',
                                             color: 'rgba(255, 255, 255, 0.35)',
                                          }}
                                       >
                                          Host · Organizer
                                       </Typography>
                                    )}
                                 </Box>
                              </Paper>
                           ))}


                           {visibleParticipants.length === 0 && (
                              <Typography
                                 sx={{
                                    textAlign: 'center',
                                    py: 4,
                                    color: 'text.secondary',
                                 }}
                              >
                                 No participants yet
                              </Typography>
                           )}
                        </Box>
                     </Box>
                  )}

                  {/* Request RSVP Tab */}
                  {activeTab === 2 && (
                     <Box>
                        <Typography
                           sx={{
                              fontSize: 15,
                              color: 'text.secondary',
                              lineHeight: 1.8,
                              mb: 3.5,
                              '& strong': {
                                 color: 'text.primary',
                              },
                           }}
                        >
                           You'll be added to the waitlist and the host will review your request. Once approved, you'll receive access to the <strong>full event dashboard</strong>, all participant albums, and the event feed.
                        </Typography>

                        {/* RSVP Form - Using existing registration modal logic */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                           <Grid container spacing={1.75}>
                              <Grid item xs={12} sm={6}>
                                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '2px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                       }}
                                    >
                                       First Name
                                    </Typography>
                                    <TextField
                                       fullWidth
                                       placeholder='Emmanuel'
                                       value={rsvpFirstName}
                                       onChange={(e) => setRsvpFirstName(e.target.value)}
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             bgcolor: 'rgba(255, 255, 255, 0.05)',
                                             borderColor: 'rgba(255, 255, 255, 0.13)',
                                             borderRadius: 1.25,
                                             '&:hover': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                             },
                                             '&.Mui-focused': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'rgba(0, 196, 167, 0.05)',
                                                boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                             },
                                             '& input': {
                                               color: 'text.primary',
                                               '&::placeholder': {
                                                  color: 'rgba(255, 255, 255, 0.18)',
                                               },
                                             },
                                          },
                                       }}
                              />
                           </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '2px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                       }}
                                    >
                                       Last Name
                                    </Typography>
                                    <TextField
                                       fullWidth
                                       placeholder='Jones'
                                       value={rsvpLastName}
                                       onChange={(e) => setRsvpLastName(e.target.value)}
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             bgcolor: 'rgba(255, 255, 255, 0.05)',
                                             borderColor: 'rgba(255, 255, 255, 0.13)',
                                             borderRadius: 1.25,
                                             '&:hover': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                             },
                                             '&.Mui-focused': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'rgba(0, 196, 167, 0.05)',
                                                boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                             },
                                             '& input': {
                                               color: 'text.primary',
                                               '&::placeholder': {
                                                  color: 'rgba(255, 255, 255, 0.18)',
                                               },
                                             },
                                          },
                                       }}
                                    />
                                 </Box>
                              </Grid>
                           </Grid>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                 }}
                              >
                                 Email Address
                              </Typography>
                              <TextField
                                 fullWidth
                                 type='email'
                                 placeholder='you@example.com'
                                 value={email}
                                 onChange={async (e) => {
                                    setEmail(e.target.value);
                                    // Check if user exists when email is entered
                                    if (e.target.value.trim() && !currentUser) {
                                       try {
                                          const response = await fetch(
                                             `${process.env.REACT_APP_API_URL}/api/auth/check-user-exists`,
                                             {
                                                method: 'POST',
                                                headers: {
                                                   'Content-Type': 'application/json',
                                                },
                                                body: JSON.stringify({ email: e.target.value }),
                                             }
                                          );
                                          const result = await response.json();
                                          setUserExists(result.exists || false);
                                       } catch (err) {
                                          // Ignore errors, just don't set userExists
                                       }
                                    }
                                 }}
                                 sx={{
                                    '& .MuiOutlinedInput-root': {
                                       bgcolor: 'rgba(255, 255, 255, 0.05)',
                                       borderColor: 'rgba(255, 255, 255, 0.13)',
                                       borderRadius: 1.25,
                                       '&:hover': {
                                          borderColor: 'rgba(255, 255, 255, 0.2)',
                                       },
                                       '&.Mui-focused': {
                                          borderColor: 'primary.main',
                                          bgcolor: 'rgba(0, 196, 167, 0.05)',
                                          boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                       },
                                       '& input': {
                                         color: 'text.primary',
                                         '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.18)',
                                         },
                                       },
                                    },
                                 }}
                              />
                           </Box>
                           <Grid container spacing={1.75}>
                              <Grid item xs={12} sm={6}>
                                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '2px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                       }}
                                    >
                                       City
                                    </Typography>
                                    <TextField
                                       fullWidth
                                       placeholder='Philadelphia, PA'
                                       value={rsvpCity}
                                       onChange={(e) => setRsvpCity(e.target.value)}
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             bgcolor: 'rgba(255, 255, 255, 0.05)',
                                             borderColor: 'rgba(255, 255, 255, 0.13)',
                                             borderRadius: 1.25,
                                             '&:hover': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                             },
                                             '&.Mui-focused': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'rgba(0, 196, 167, 0.05)',
                                                boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                             },
                                             '& input': {
                                               color: 'text.primary',
                                               '&::placeholder': {
                                                  color: 'rgba(255, 255, 255, 0.18)',
                                               },
                                             },
                                          },
                                       }}
                                    />
                                 </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '2px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                       }}
                                    >
                                       Your Role
                                    </Typography>
                                    <TextField
                                       fullWidth
                                       select
                                       value={rsvpRole}
                                       onChange={(e) => setRsvpRole(e.target.value)}
                                       SelectProps={{
                                          native: true,
                                       }}
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             bgcolor: 'rgba(255, 255, 255, 0.05)',
                                             borderColor: 'rgba(255, 255, 255, 0.13)',
                                             borderRadius: 1.25,
                                             '&:hover': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                             },
                                             '&.Mui-focused': {
                                                borderColor: 'primary.main',
                                                boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                             },
                                             '& select': {
                                               color: 'text.primary',
                                             },
                                          },
                                       }}
                                    >
                                       <option value=''>Select a role…</option>
                                       <option value='model'>Model</option>
                                       <option value='photographer'>Photographer</option>
                                       <option value='attendee'>Attendee / Guest</option>
                                       <option value='vendor'>Vendor / Exhibitor</option>
                                       <option value='press'>Press / Media</option>
                                    </TextField>
                                 </Box>
                              </Grid>
                           </Grid>
                           {!currentUser && (
                              <>
                                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 9,
                                          letterSpacing: '2px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.35)',
                                       }}
                                    >
                                       Password
                                    </Typography>
                                    <TextField
                                       fullWidth
                                       type='password'
                                       placeholder={userExists ? 'Enter your password' : 'Create a password'}
                                       value={rsvpPassword}
                                       onChange={(e) => setRsvpPassword(e.target.value)}
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             bgcolor: 'rgba(255, 255, 255, 0.05)',
                                             borderColor: 'rgba(255, 255, 255, 0.13)',
                                             borderRadius: 1.25,
                                             '&:hover': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                             },
                                             '&.Mui-focused': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'rgba(0, 196, 167, 0.05)',
                                                boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                             },
                                             '& input': {
                                               color: 'text.primary',
                                               '&::placeholder': {
                                                  color: 'rgba(255, 255, 255, 0.18)',
                                               },
                                             },
                                          },
                                       }}
                                    />
                                 </Box>
                                 {!userExists && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                       <Typography
                                          sx={{
                                             fontFamily: FONT_MONO,
                                             fontSize: 9,
                                             letterSpacing: '2px',
                                             textTransform: 'uppercase',
                                             color: 'rgba(255, 255, 255, 0.35)',
                                          }}
                                       >
                                          Confirm Password
                                       </Typography>
                                       <TextField
                                          fullWidth
                                          type='password'
                                          placeholder='Confirm your password'
                                          value={rsvpConfirmPassword}
                                          onChange={(e) => setRsvpConfirmPassword(e.target.value)}
                                          sx={{
                                             '& .MuiOutlinedInput-root': {
                                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                                                borderColor: 'rgba(255, 255, 255, 0.13)',
                                                borderRadius: 1.25,
                                                '&:hover': {
                                                   borderColor: 'rgba(255, 255, 255, 0.2)',
                                                },
                                                '&.Mui-focused': {
                                                   borderColor: 'primary.main',
                                                   bgcolor: 'rgba(0, 196, 167, 0.05)',
                                                   boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                                },
                                                '& input': {
                                                  color: 'text.primary',
                                                  '&::placeholder': {
                                                     color: 'rgba(255, 255, 255, 0.18)',
                                                  },
                                                },
                                             },
                                          }}
                              />
                           </Box>
                        )}
                              </>
                           )}
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                 }}
                              >
                                 Message to Host{' '}
                                 <Typography component='span' sx={{ color: 'rgba(255, 255, 255, 0.18)', fontSize: 9 }}>
                                    (optional)
                                 </Typography>
                              </Typography>
                              <TextField
                                 fullWidth
                                 multiline
                                 rows={3}
                                 placeholder="Tell the host a little about yourself and why you'd like to attend…"
                                 value={rsvpMessage}
                                 onChange={(e) => setRsvpMessage(e.target.value)}
                                 sx={{
                                    '& .MuiOutlinedInput-root': {
                                       bgcolor: 'rgba(255, 255, 255, 0.05)',
                                       borderColor: 'rgba(255, 255, 255, 0.13)',
                                       borderRadius: 1.25,
                                       '&:hover': {
                                          borderColor: 'rgba(255, 255, 255, 0.2)',
                                       },
                                       '&.Mui-focused': {
                                          borderColor: 'primary.main',
                                          bgcolor: 'rgba(0, 196, 167, 0.05)',
                                          boxShadow: '0 0 0 3px rgba(0, 196, 167, 0.12)',
                                       },
                                       '& textarea': {
                                         color: 'text.primary',
                                         '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.18)',
                                         },
                                       },
                                    },
                                 }}
                              />
                           </Box>
                           <Button
                              variant='contained'
                              onClick={handleRSVPFormSubmit}
                              disabled={submitting || !rsvpFirstName.trim() || !rsvpLastName.trim() || !email.trim() || !rsvpRole.trim() || (!currentUser && !rsvpPassword.trim())}
                              sx={{
                                 bgcolor: 'primary.main',
                                 color: '#000',
                                 px: 4,
                                 py: 1.875,
                                 borderRadius: 1.25,
                                 fontSize: 15,
                                 fontWeight: 700,
                                 textTransform: 'none',
                                 boxShadow: '0 8px 24px rgba(0,196,167,0.22)',
                                 mt: 0.5,
                                 '&:hover': {
                                    bgcolor: '#00DFC0',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 12px 32px rgba(0,196,167,0.32)',
                                 },
                              }}
                           >
                              {submitting ? (
                                 <>
                                    <CircularProgress size={20} sx={{ color: '#000', mr: 1 }} />
                                    Submitting...
                                 </>
                              ) : (
                                 'Submit RSVP Request →'
                              )}
                           </Button>
                        </Box>
                     </Box>
                  )}
               </Grid>

               {/* Right Sidebar */}
               <Grid item xs={12} md={4}>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.75,
                        position: { xs: 'static', md: 'sticky' },
                        top: { md: 72 },
                     }}
                  >
                     {/* Countdown Card */}
                     {event.startDate && (
                        <Paper
                           sx={{
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 2.5,
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 1,
                                 mb: 1.75,
                              }}
                           >
                              <Typography
                                 sx={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    letterSpacing: '2.5px',
                                    textTransform: 'uppercase',
                                    color: 'primary.main',
                                 }}
                              >
                                 Event Starts In
                              </Typography>
                              <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(0, 196, 167, 0.10)' }} />
                           </Box>
                           <Grid container spacing={0.75} sx={{ mb: 1.75 }}>
                              <Grid item xs={3}>
                                 <Paper
                                    sx={{
                                       bgcolor: 'background.paper',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       borderRadius: 1.25,
                                       p: 1.25,
                                       textAlign: 'center',
                                    }}
                                 >
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_DISPLAY,
                                          fontSize: 26,
                                          fontWeight: 300,
                                          color: 'primary.main',
                                          lineHeight: 1,
                                       }}
                                    >
                                       {String(countdown.days).padStart(2, '0')}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 8,
                                          letterSpacing: '1px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.18)',
                                          mt: 0.375,
                                       }}
                                    >
                                       Days
                                    </Typography>
                                 </Paper>
                              </Grid>
                              <Grid item xs={3}>
                                 <Paper
                                    sx={{
                                       bgcolor: 'background.paper',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       borderRadius: 1.25,
                                       p: 1.25,
                                       textAlign: 'center',
                                    }}
                                 >
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_DISPLAY,
                                          fontSize: 26,
                                          fontWeight: 300,
                                          color: 'primary.main',
                                          lineHeight: 1,
                                       }}
                                    >
                                       {String(countdown.hours).padStart(2, '0')}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 8,
                                          letterSpacing: '1px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.18)',
                                          mt: 0.375,
                                       }}
                                    >
                                       Hrs
                                    </Typography>
                                 </Paper>
                              </Grid>
                              <Grid item xs={3}>
                                 <Paper
                                    sx={{
                                       bgcolor: 'background.paper',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       borderRadius: 1.25,
                                       p: 1.25,
                                       textAlign: 'center',
                                    }}
                                 >
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_DISPLAY,
                                          fontSize: 26,
                                          fontWeight: 300,
                                          color: 'primary.main',
                                          lineHeight: 1,
                                       }}
                                    >
                                       {String(countdown.minutes).padStart(2, '0')}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 8,
                                          letterSpacing: '1px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.18)',
                                          mt: 0.375,
                                       }}
                                    >
                                       Min
                                    </Typography>
                                 </Paper>
                              </Grid>
                              <Grid item xs={3}>
                                 <Paper
                                    sx={{
                                       bgcolor: 'background.paper',
                                       border: '1px solid',
                                       borderColor: 'divider',
                                       borderRadius: 1.25,
                                       p: 1.25,
                                       textAlign: 'center',
                                    }}
                                 >
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_DISPLAY,
                                          fontSize: 26,
                                          fontWeight: 300,
                                          color: 'primary.main',
                                          lineHeight: 1,
                                       }}
                                    >
                                       {String(countdown.seconds).padStart(2, '0')}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          fontFamily: FONT_MONO,
                                          fontSize: 8,
                                          letterSpacing: '1px',
                                          textTransform: 'uppercase',
                                          color: 'rgba(255, 255, 255, 0.18)',
                                          mt: 0.375,
                                       }}
                                    >
                                       Sec
                                    </Typography>
                                 </Paper>
                              </Grid>
                           </Grid>
                           <Button
                              variant='contained'
                              fullWidth
                              onClick={() => {
                                 setActiveTab(2);
                                 if (!currentUser) {
                                    handleRSVPRequest();
                                 }
                              }}
                              sx={{
                                 bgcolor: 'primary.main',
                                 color: '#000',
                                 py: 1.5,
                                 borderRadius: 1.25,
                                 fontSize: 13,
                                 fontWeight: 700,
                                 textTransform: 'none',
                              }}
                           >
                              ＋ Request RSVP
                           </Button>
                        </Paper>
                     )}

                     {/* Attendees Card */}
                     <Paper
                        sx={{
                           bgcolor: 'background.paper',
                           border: '1px solid',
                           borderColor: 'divider',
                           borderRadius: 2,
                           p: 2.5,
                        }}
                     >
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.25,
                           }}
                        >
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 9,
                                 letterSpacing: '2.5px',
                                 textTransform: 'uppercase',
                                 color: 'primary.main',
                              }}
                           >
                              Attendees
                           </Typography>
                           <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(0, 196, 167, 0.10)' }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                           {visibleParticipants.slice(0, 5).map((p: any, idx: number) => {
                              const photoUrl =
                                 p.photoURL ||
                                 p.profileData?.photoURL ||
                                 p.profileData?.profilePicture ||
                                 p.profileData?.avatar ||
                                 p.profileData?.profilePhoto;
                              return (
                                 <Avatar
                                    key={idx}
                                    src={photoUrl || undefined}
                                    sx={{
                                       width: 36,
                                       height: 36,
                                       bgcolor: photoUrl ? 'transparent' : 'primary.main',
                                       color: photoUrl ? 'inherit' : '#000',
                                       fontSize: 13,
                                       fontWeight: 700,
                                    }}
                                 >
                                    {!photoUrl && (p.displayName?.charAt(0)?.toUpperCase() || 'U')}
                                 </Avatar>
                              );
                           })}
                           {attendeeCount > 5 && (
                              <Avatar
                                 sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: 'background.elevated',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    color: 'rgba(255, 255, 255, 0.35)',
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                 }}
                              >
                                 +{attendeeCount - 5}
                              </Avatar>
                           )}
                        </Box>
                        <Typography
                           sx={{
                              fontSize: 12,
                              color: 'rgba(255, 255, 255, 0.35)',
                              lineHeight: 1.6,
                           }}
                        >
                           <Typography component='span' sx={{ color: 'rgba(255, 255, 255, 0.60)', fontWeight: 600 }}>
                              {attendeeCount} people
                           </Typography>{' '}
                           have requested to attend ·{' '}
                           <Typography
                              component='span'
                              sx={{
                                 color: 'primary.main',
                                 cursor: 'pointer',
                                 '&:hover': {
                                    textDecoration: 'underline',
                                 },
                              }}
                              onClick={() => setActiveTab(1)}
                           >
                              View all
                           </Typography>
                        </Typography>
                     </Paper>

                     {/* Share Card */}
                     <Paper
                        sx={{
                           bgcolor: 'background.paper',
                           border: '1px solid',
                           borderColor: 'divider',
                           borderRadius: 2,
                           p: 2.5,
                        }}
                     >
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.75,
                           }}
                        >
                           <Typography
                              sx={{
                                 fontFamily: FONT_MONO,
                                 fontSize: 9,
                                 letterSpacing: '2.5px',
                                 textTransform: 'uppercase',
                                 color: 'primary.main',
                              }}
                           >
                              Share Event
                           </Typography>
                           <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(0, 196, 167, 0.10)' }} />
                        </Box>
                        <Button
                           fullWidth
                           variant={copiedLink ? 'contained' : 'outlined'}
                           onClick={handleCopyLink}
                           startIcon={copiedLink ? <CheckIcon /> : <LinkIcon />}
                           sx={{
                              bgcolor: copiedLink
                                 ? 'rgba(0, 196, 167, 0.10)'
                                 : 'rgba(255, 255, 255, 0.04)',
                              borderColor: copiedLink
                                 ? 'rgba(0, 196, 167, 0.22)'
                                 : 'divider',
                              color: copiedLink ? 'primary.main' : 'text.secondary',
                              py: 1.25,
                              px: 1.75,
                              borderRadius: 1.125,
                              fontSize: 12,
                              fontWeight: 500,
                              textTransform: 'none',
                              justifyContent: 'flex-start',
                              '&:hover': {
                                 borderColor: copiedLink
                                    ? 'rgba(0, 196, 167, 0.3)'
                                    : 'rgba(255, 255, 255, 0.13)',
                                 color: copiedLink ? 'primary.main' : 'text.primary',
                                 bgcolor: copiedLink
                                    ? 'rgba(0, 196, 167, 0.16)'
                                    : 'rgba(255, 255, 255, 0.06)',
                              },
                           }}
                        >
                           {copiedLink ? '✓ Copied!' : 'Copy Event Link'}
                        </Button>
                     </Paper>
                  </Box>
               </Grid>
            </Grid>
         </Container>

         {/* Footer */}
         <Box
            sx={{
               borderTop: '1px solid',
               borderColor: 'divider',
               mt: 5,
               py: 3.5,
               px: { xs: 2, md: 5 },
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'space-between',
            }}
         >
            <Typography
               sx={{
                  fontFamily: FONT_DISPLAY,
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.35)',
                  '& span': {
                     color: 'primary.main',
                  },
               }}
            >
               We<span>Trippen</span>
            </Typography>
            <Typography
               sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 9,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  color: 'rgba(255, 255, 255, 0.18)',
               }}
            >
               © 2026 WeTrippen · All rights reserved
            </Typography>
         </Box>

         {/* Modern RSVP Modal */}
         <Dialog
            open={rsvpModalOpen}
            onClose={() => setRsvpModalOpen(false)}
            maxWidth='sm'
            fullWidth
            PaperProps={{
               sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 196, 167, 0.2)' },
            }}
         >
            <DialogTitle
               sx={{
                  background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                  color: 'white',
                  py: 2,
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6' sx={{ color: 'white', fontWeight: 600 }}>
                     Request to Join Event
                  </Typography>
                  <IconButton onClick={() => setRsvpModalOpen(false)} sx={{ color: 'white' }}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
               <Typography variant='body2' sx={{ mb: 3, color: '#6B7280' }}>
                  Enter your email address to request joining this event.
               </Typography>
               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                     },
                  }}
               />
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
               <Button onClick={() => setRsvpModalOpen(false)} sx={{ color: '#6B7280' }}>
                  Cancel
               </Button>
               <Button
                  onClick={handleRSVPSubmit}
                  variant='contained'
                  disabled={submitting}
                  sx={{
                     background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                     },
                     px: 3,
                     py: 1,
                  }}
               >
                  {submitting ? <CircularProgress size={24} /> : 'Continue'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Registration/Login Modal */}
         <Dialog
            open={registrationModalOpen}
            onClose={() => setRegistrationModalOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>Request to Join Event</Typography>
                  <IconButton onClick={() => setRegistrationModalOpen(false)}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your details to request joining this event.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2 }}>
               <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button onClick={() => setRegistrationModalOpen(false)} sx={{ flex: 1 }}>
                     Cancel
                  </Button>
                  <Button onClick={handleLogin} variant='outlined' sx={{ flex: 1 }} disabled={submitting}>
                     Login Instead
                  </Button>
               </Box>
               <Button
                  onClick={handleRegistration}
                  variant='contained'
                  fullWidth
                  disabled={submitting}
               >
                  {submitting ? <CircularProgress size={24} /> : 'Request'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* EventSignupModal */}
         <EventSignupModal
            open={signupModalOpen}
            onClose={() => setSignupModalOpen(false)}
            eventId={eventId!}
            onSignupSuccess={handleSignupSuccess}
         />

         {/* Snackbar */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default PublicEventPage;
